import React from "react";
import { Spinner } from "react-bootstrap";
import { LuArrowRight } from "react-icons/lu";
import { TotalOrdersCardProps } from "../types";

const TotalOrdersCard: React.FC<TotalOrdersCardProps> = ({
  summary,
  summaryLoading,
  onViewAll,
}) => {
  const breakdown = summary?.total_orders_with_breakdown;
  const delivered = breakdown?.delivered ?? 0;
  const pending = breakdown?.pending ?? 0;
  const cancelled = breakdown?.cancelled ?? 0;
  const others = breakdown?.others ?? 0;
  const total =
    breakdown?.total ?? delivered + pending + cancelled + others;

  return (
    <div className="bg-white rounded-4 border border-1 border-light-subtle p-4 shadow-sm h-100 d-flex flex-column justify-content-between">
      {/* Top Header */}
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-secondary small fw-medium">
            Total orders
          </span>
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none text-primary small d-flex align-items-center gap-1 fw-semibold"
            onClick={onViewAll}
          >
            View all <LuArrowRight size={13} />
          </button>
        </div>

        {/* Total Number */}
        <div className="display-6 fw-bold text-dark mb-4">
          {summaryLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            (total?.toLocaleString() ?? 0)
          )}
        </div>
      </div>

      {/* Progress Bar & Status Breakdown */}
      <div>
        {/* Multi-segment Pill Bar */}
        <div
          className="w-100 d-flex align-items-center mb-3"
          style={{
            height: 10,
            backgroundColor: "#DCECE2",
            borderRadius: 9999,
            overflow: "hidden",
            gap: 3,
            padding: "0 1px",
          }}
        >
          {total > 0 && !summaryLoading && (
            <>
              {delivered > 0 && (
                <div
                  style={{
                    width: `${(delivered / total) * 100}%`,
                    minWidth: 8,
                    height: "100%",
                    backgroundColor: "#0CC99E",
                    borderRadius: 9999,
                    transition: "width 0.4s ease",
                  }}
                  title={`Delivered: ${delivered}`}
                />
              )}
              {pending > 0 && (
                <div
                  style={{
                    width: `${(pending / total) * 100}%`,
                    minWidth: 8,
                    height: "100%",
                    backgroundColor: "#F49E0C",
                    borderRadius: 9999,
                    transition: "width 0.4s ease",
                  }}
                  title={`Pending: ${pending}`}
                />
              )}
              {cancelled > 0 && (
                <div
                  style={{
                    width: `${(cancelled / total) * 100}%`,
                    minWidth: 8,
                    height: "100%",
                    backgroundColor: "#FD0606",
                    borderRadius: 9999,
                    transition: "width 0.4s ease",
                  }}
                  title={`Cancelled: ${cancelled}`}
                />
              )}
              {others > 0 && (
                <div
                  style={{
                    width: `${(others / total) * 100}%`,
                    minWidth: 8,
                    height: "100%",
                    backgroundColor: "#6366F1",
                    borderRadius: 9999,
                    transition: "width 0.4s ease",
                  }}
                  title={`Others: ${others}`}
                />
              )}
            </>
          )}
        </div>

        {/* Legend */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: "#0CC99E",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span className="small fw-medium text-dark">
              Delivered ({summaryLoading ? "-" : delivered})
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: "#F49E0C",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span className="small fw-medium text-dark">
              Pending ({summaryLoading ? "-" : pending})
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: "#FD0606",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span className="small fw-medium text-dark">
              Cancelled ({summaryLoading ? "-" : cancelled})
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: "#6366F1",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span className="small fw-medium text-dark">
              Others ({summaryLoading ? "-" : others})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TotalOrdersCard);
